import { useRef, useState } from "react";
import { Camera } from "lucide-react";
import { SevBadge } from "./Bits";
import { Input } from "./ui/input";
import PhotoAnnotator from "./PhotoAnnotator";
import { photoSrc, saveLocalPhoto } from "../lib/api";
import { readFile } from "../lib/image";

const OPTS = [["RESOLVED", "Resolved", "bg-emerald-600"], ["PARTIAL", "Partially", "bg-amber-500"], ["NOT_RESOLVED", "Not resolved", "bg-rose-600"]];

function Row({ d, ins, onChange }) {
  const cam = useRef(null);
  const [ann, setAnn] = useState(null);
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`reinspect-${d.number}`}>
      <div className="flex items-center justify-between"><span className="font-mono text-xs font-bold">{d.number} · {d.room}</span><SevBadge sev={d.severity} /></div>
      <div className="mt-1 font-semibold">{d.name}</div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <div><div className="mb-1 font-mono text-[10px] text-slate-400">PREVIOUS PHOTO</div>{d.photos?.[0] ? <img src={photoSrc(d.photos[0])} alt="before" className="aspect-[4/3] w-full rounded-md object-cover" /> : <div className="aspect-[4/3] rounded-md bg-slate-100" />}</div>
        <div><div className="mb-1 font-mono text-[10px] text-slate-400">NEW PHOTO</div>
          <button onClick={() => cam.current.click()} className="tap grid aspect-[4/3] w-full place-items-center overflow-hidden rounded-md border-2 border-dashed border-slate-300" data-testid={`reinspect-photo-${d.number}`}>
            {d.new_photos?.[0] ? <img src={photoSrc(d.new_photos[0])} alt="after" className="h-full w-full object-cover" /> : <Camera className="h-6 w-6 text-slate-400" />}
          </button></div>
      </div>
      <input ref={cam} type="file" accept="image/*" capture="environment" className="hidden" onChange={async (e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) setAnn(await readFile(f)); }} />
      {d.builder_fix && (
        <div className="mt-3 flex gap-3 rounded-md border border-emerald-200 bg-emerald-50 p-2 text-xs" data-testid={`reinspect-builder-fix-${d.number}`}>
          {d.builder_fix.photos?.[0] && <img src={photoSrc(d.builder_fix.photos[d.builder_fix.photos.length - 1])} alt="builder fix" className="h-16 w-20 rounded object-cover" />}
          <div><div className="font-mono text-[10px] font-bold text-emerald-800">BUILDER: {d.builder_fix.status?.replace("_", " ")}</div>{d.builder_fix.note || "No note"}</div>
        </div>
      )}
      <p className="mt-3 text-sm text-slate-600"><b>Previous remark:</b> {d.remark}</p>
      <p className="text-sm text-slate-600"><b>Previous recommendation:</b> {d.recommendation}</p>
      <div className="mt-3 grid grid-cols-3 gap-1.5">
        {OPTS.map(([k, l, c]) => <button key={k} onClick={() => onChange({ ...d, re_status: k })} className={`tap h-12 rounded-md border text-xs font-bold ${d.re_status === k ? `${c} border-transparent text-white` : "border-slate-200"}`} data-testid={`reinspect-${d.number}-${k.toLowerCase()}`}>{l.toUpperCase()}</button>)}
      </div>
      <Input className="mt-2 h-11" placeholder="Current condition note" value={d.re_note || ""} onChange={(e) => onChange({ ...d, re_note: e.target.value })} data-testid={`reinspect-${d.number}-note`} />
      <PhotoAnnotator open={!!ann} src={ann} stamp={`${ins.inspection_id} | ${d.room} | RE ${d.number} | ${new Date().toLocaleString("en-IN")}`} onCancel={() => setAnn(null)}
        onRetake={() => { setAnn(null); cam.current.click(); }} onSave={(u) => { onChange({ ...d, new_photos: [saveLocalPhoto(u)] }); setAnn(null); }} />
    </div>
  );
}

export default function ReinspectionPanel({ ins, update, onNewDefect }) {
  const od = ins.original_defects || [];
  const done = od.filter((d) => d.re_status).length;
  const change = (nd) => update((x) => ({ ...x, original_defects: x.original_defects.map((d) => (d.defect_id === nd.defect_id ? nd : d)) }));
  return (
    <div className="space-y-3" data-testid="reinspection-panel">
      <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white p-4">
        <div><div className="overline">Re-inspection of {ins.parent_id}</div><div className="mt-1 font-semibold">{done} / {od.length} original defects checked</div></div>
        <button onClick={onNewDefect} className="tap h-11 rounded-md bg-slate-900 px-4 text-sm font-bold text-white" data-testid="reinspect-new-defect-button">+ NEW DEFECT</button>
      </div>
      {od.map((d) => <Row key={d.defect_id} d={d} ins={ins} onChange={change} />)}
    </div>
  );
}
